import { secureApiService } from './apiService';
import { isValidEmail } from '../utils/emailValidation'; // M2-L1 — source unique (plus de regex inline)

// 🔒 VALIDATION DES DONNÉES DE RÉSERVATION
const validateReservationData = (data) => {
  const sanitized = { ...data };
  
  // Validation email
  if (sanitized.email && !isValidEmail(sanitized.email)) {
    throw new Error('Email invalide');
  }
  
  // Validation téléphone
  if (sanitized.telephone) {
    sanitized.telephone = sanitized.telephone.replace(/[^\d+]/g, '');
    if (sanitized.telephone.length > 20) {
      throw new Error('Numéro de téléphone trop long');
    }
  }
  
  // Validation noms
  if (sanitized.nom) {
    sanitized.nom = sanitized.nom.replace(/[<>]/g, '').substring(0, 50);
  }
  
  if (sanitized.prenom) {
    sanitized.prenom = sanitized.prenom.replace(/[<>]/g, '').substring(0, 50);
  }
  
  return sanitized;
};

// 🔒 VALIDATION EMAIL : isValidEmail importé en tête (source unique partagée).

// 🔒 VALIDATION ID RÉSERVATION
const validateReservationId = (id) => {
  if (!id && id !== 0) {
    throw new Error('ID de réservation invalide');
  }
  const str = String(id);
  if (!str || str === 'undefined' || str === 'null') {
    throw new Error('ID de réservation invalide');
  }
  return str;
};

// 🔒 VALIDATION ID TRAJET
const validateTrajetId = (id) => {
  if (!id && id !== 0) {
    throw new Error('ID de trajet invalide');
  }
  const str = String(id);
  if (!str || str === 'undefined' || str === 'null') {
    throw new Error('ID de trajet invalide');
  }
  return str;
};

export const reservationService = {
  // 📋 RÉSERVATIONS UTILISATEUR
  getMyReservations: () => 
    secureApiService.get('/reservations/mes-reservations', {}, { 
      cacheTimeout: 30000 
    }),

  // 📜 HISTORIQUE RÉSERVATIONS
  getReservationHistory: () => 
    secureApiService.get('/reservations/historique', {}, { 
      cacheTimeout: 60000 
    }),


  // 👀 DÉTAILS RÉSERVATION
  getReservationById: (id) => {
    const validId = validateReservationId(id);
    return secureApiService.get(`/reservations/${validId}`, {}, { 
      useCache: false 
    });
  },

  // ❌ ANNULATION RÉSERVATION - PUT /reservations/:id/annuler
  cancelReservation: (id, reason = "") => {
    const validId = validateReservationId(id);
    const sanitizedReason = reason.replace(/[<>]/g, '').substring(0, 200);
    return secureApiService.put(`/reservations/${validId}/annuler`, {
      reason: sanitizedReason
    });
  },

  // 🚌 RÉSERVER UN TRAJET
  reserverTrajet: (trajetId, data) => {
    const validTrajetId = validateTrajetId(trajetId);
    const sanitizedData = validateReservationData(data);
    return secureApiService.post(`/trajets/${validTrajetId}/reserver`, {
      siege_numero: sanitizedData.siege_numero,
      moyen_paiement: sanitizedData.moyen_paiement,
      arret_depart: sanitizedData.arret_depart,
      arret_arrivee: sanitizedData.arret_arrivee,
      prix_calcule: sanitizedData.prix_calcule || sanitizedData.prix_total
    }, { timeout: 30000 });
  },

  // 🎫 RÉSERVATION INVITÉ (checkout public sans connexion) — Nom/Prénom + Téléphone OU Email.
  // Renvoie un token client + pose le cookie de session : le pipeline paiement/ticket suit.
  reserverTrajetInvite: (trajetId, data) => {
    const validTrajetId = validateTrajetId(trajetId);
    const sanitizedData = validateReservationData(data);
    return secureApiService.post(`/trajets/${validTrajetId}/reserver-invite`, {
      nom: sanitizedData.nom,
      prenom: sanitizedData.prenom,
      telephone: sanitizedData.telephone || null,
      email: sanitizedData.email || null,
      creer_compte: !!data.creer_compte,
      conditions_acceptees: !!data.conditions_acceptees,
      siege_numero: sanitizedData.siege_numero,
      moyen_paiement: sanitizedData.moyen_paiement,
      arret_depart: sanitizedData.arret_depart,
      arret_arrivee: sanitizedData.arret_arrivee,
      prix_calcule: sanitizedData.prix_calcule || sanitizedData.prix_total
    }, { timeout: 30000 });
  },

  // 📋 LISTE DES RÉSERVATIONS (pour l'admin ou autre)
  getReservations: (params = {}) => {
    return secureApiService.get('/reservations', { params }, {
      cacheTimeout: 30000
    });
  },

  getReservationDetails: (id) => {
    const validId = validateReservationId(id);
    return secureApiService.get(`/reservations/${validId}`, {}, { 
      cacheTimeout: 30000 
    });
  },

  // 📧 RENVOI BILLET
  resendTicket: (id) => {
    const validId = validateReservationId(id);
    return secureApiService.post(`/reservations/${validId}/resend-ticket`);
  },

  // 🎫 RETROUVER MON BILLET (invité, sans compte) : code de réservation + email OU téléphone.
  // Renvoie { token (jeton invité éphémère), payment_id } pour télécharger le billet.
  retrouverBillet: (code, contact) =>
    secureApiService.post('/reservations/retrouver-billet', { code, contact }),

  // 🎫 TÉLÉCHARGEMENT BILLET
  downloadTicket: (id) => {
    const validId = validateReservationId(id);
    return secureApiService.get(`/reservations/${validId}/ticket`, {}, { 
      useCache: false, 
      responseType: 'blob',
      timeout: 60000
    });
  },
  
  // 🔍 VÉRIFIER DISPONIBILITÉ D'UN SIÈGE
  checkSeatAvailability: (trajetId, seatNumber) => {
    const validTrajetId = validateTrajetId(trajetId);
    return secureApiService.get(`/trajets/${validTrajetId}/sieges/disponible`, {
      params: { siege: seatNumber }
    }, { 
      useCache: false,
      timeout: 10000 
    });
  },
  
  // 🔄 METTRE À JOUR UNE RÉSERVATION
  updateReservation: (reservationId, data) => {
    const validId = validateReservationId(reservationId);
    const sanitizedData = validateReservationData(data);
    return secureApiService.put(`/reservations/${validId}`, sanitizedData);
  }
};